import { useEffect, useRef, useState } from 'react';
import { type QueryDocumentSnapshot, type DocumentData } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  getReferencesForBrandPaginated,
  searchReferencesInBrand,
  getImageUrl,
  getWatchReference,
  type ReferenceSort,
} from '../../services/firebase';
import type { WatchReference } from '../../types';
import { Modal } from '../common/Modal';
import { EditMyReferencePanel } from './EditMyReferencePanel';

/**
 * MyReferencesPanel — Brand Portal Phase 2.2b.
 *
 * A list of a brand's references for the brand owner. Scoped to the `brandId`
 * the parent page already authorized (BrandPortalPage gates access, so no extra
 * authz is needed here). Tapping a row re-fetches the FULL reference doc (list
 * rows may carry incomplete nested data) and opens `EditMyReferencePanel` in a
 * `<Modal>` for whole-replace editing (2.2c chunk iv).
 *
 * Data comes straight from the existing Firestore helpers:
 *   - browse: `getReferencesForBrandPaginated` (cursor-based, orderBy reference)
 *   - search: `searchReferencesInBrand` (fetches up to 500, filters client-side)
 * Algolia-backed list/search is a 2.2d concern and intentionally not used here.
 */

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Browse-list sort options (2.2d chunk iii). Keyed by a stable string so the
// browse effect can depend on the key (not a fresh object identity each render).
// Default is `newest` so refs just created via the "+ New reference" flow land
// at the top of page 1. "Model name" is intentionally omitted: the existing
// list sorts by `reference` only (no `model` secondary sort), and the brief
// said to skip-and-flag rather than expose it in that case — see handoff notes.
const SORT_OPTIONS = {
  newest: {
    label: 'Newest first',
    sort: { field: 'createdAt', direction: 'desc' },
  },
  oldest: {
    label: 'Oldest first',
    sort: { field: 'createdAt', direction: 'asc' },
  },
  reference: {
    label: 'Reference number',
    sort: { field: 'reference', direction: 'asc' },
  },
} as const satisfies Record<string, { label: string; sort: ReferenceSort }>;

type SortKey = keyof typeof SORT_OPTIONS;
// Default to `reference` (always-present field) so no refs are ever hidden by
// the Firestore createdAt missing-field exclusion. The createdAt-based sorts
// (newest/oldest) are opt-in and surface a warning banner — see below.
const DEFAULT_SORT_KEY: SortKey = 'reference';

interface MyReferencesPanelProps {
  brandId: string;
}

// ── Row ──────────────────────────────────────────────────────────────────────

function ReferenceRow({
  reference,
  onSelect,
  loading,
}: {
  reference: WatchReference;
  onSelect: (reference: WatchReference) => void;
  loading: boolean;
}) {
  // Prefer an already-resolved direct URL; otherwise resolve the first storage
  // path (same approach as the admin ReferenceBrowser row).
  const [imageUrl, setImageUrl] = useState<string | null>(
    reference.imageUrls?.[0] ?? null,
  );

  useEffect(() => {
    if (reference.imageUrls?.[0]) {
      setImageUrl(reference.imageUrls[0]);
      return;
    }
    const storagePath = reference.images?.[0]?.storagePath;
    if (!storagePath) {
      setImageUrl(null);
      return;
    }
    let cancelled = false;
    getImageUrl(storagePath)
      .then((url) => {
        if (!cancelled) setImageUrl(url);
      })
      .catch(() => {
        if (!cancelled) setImageUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reference.imageUrls, reference.images]);

  const caseSize =
    reference.case?.diameter ??
    reference.case?.sizeMm ??
    reference.case?.diameterMm;
  // Small metadata pill: case size if known, else collection.
  const pill = caseSize ? `${caseSize}mm` : reference.collection || null;

  return (
    <li>
      <button
        type='button'
        onClick={() => onSelect(reference)}
        disabled={loading}
        className='w-full flex items-center gap-4 px-6 py-3 text-left hover:bg-gray-50 disabled:opacity-60'
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={reference.reference || reference.id}
            className='h-10 w-10 rounded object-cover bg-gray-50 border border-gray-200 flex-shrink-0'
          />
        ) : (
          <div className='h-10 w-10 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0'>
            <svg
              className='w-5 h-5 text-gray-300'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          </div>
        )}
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-gray-900 truncate'>
            {reference.reference || reference.id}
          </p>
          <p className='text-xs text-gray-500 truncate'>
            {reference.productName || reference.model || '—'}
          </p>
        </div>
        {pill && (
          <span className='text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0'>
            {pill}
          </span>
        )}
        {loading && (
          <span
            className='h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-brand-600'
            aria-label='Loading reference…'
          />
        )}
      </button>
    </li>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function MyReferencesPanel({ brandId }: MyReferencesPanelProps) {
  const [references, setReferences] = useState<WatchReference[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searching, setSearching] = useState(false);

  // Browse-list sort. In-memory only (resets on full reload) — matches the
  // brief's "component state" default; no localStorage persistence.
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);

  // Edit-modal: the full reference doc to edit, plus which row is mid-fetch.
  const [selectedReference, setSelectedReference] =
    useState<WatchReference | null>(null);
  const [loadingFullRefId, setLoadingFullRefId] = useState<string | null>(null);

  // Create-modal: open flag + a ref tracking whether a create actually landed
  // while it was open, so we know to re-query the canonical first page on close.
  const [isCreating, setIsCreating] = useState(false);
  const createdInModalRef = useRef(false);

  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

  // Re-query the first browse page (used by initial load, search-clear, and the
  // refresh-after-create on modal close so server-side fields show through).
  const reloadFirstPage = () => {
    setLoading(true);
    setError(false);
    setSearchTerm('');
    setIsSearchMode(false);
    lastDocRef.current = null;
    getReferencesForBrandPaginated(
      brandId,
      PAGE_SIZE,
      null,
      SORT_OPTIONS[sortKey].sort,
    )
      .then((result) => {
        setReferences(result.references);
        setTotalCount(result.totalCount);
        setHasMore(result.hasMore);
        lastDocRef.current = result.lastDoc;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  // Initial / browse load. Re-runs when `brandId` OR the sort changes, so
  // switching sort re-queries from page 1 (cursor reset to null below). The
  // sort onChange handler clears search state first, so this stays the single
  // owner of the browse page-1 fetch even when leaving search mode via sort.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    lastDocRef.current = null;

    getReferencesForBrandPaginated(
      brandId,
      PAGE_SIZE,
      null,
      SORT_OPTIONS[sortKey].sort,
    )
      .then((result) => {
        if (cancelled) return;
        setReferences(result.references);
        setTotalCount(result.totalCount);
        setHasMore(result.hasMore);
        lastDocRef.current = result.lastDoc;
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brandId, sortKey]);

  // Debounced search: empty term restores the browse list (handled by the
  // initial-load effect re-running is not possible here, so we refetch inline).
  useEffect(() => {
    const term = searchTerm.trim();

    if (term === '') {
      // Leaving search mode → restore the first browse page.
      if (!isSearchMode) return;
      let cancelled = false;
      setIsSearchMode(false);
      setSearching(false);
      setLoading(true);
      setError(false);
      lastDocRef.current = null;
      getReferencesForBrandPaginated(
        brandId,
        PAGE_SIZE,
        null,
        SORT_OPTIONS[sortKey].sort,
      )
        .then((result) => {
          if (cancelled) return;
          setReferences(result.references);
          setTotalCount(result.totalCount);
          setHasMore(result.hasMore);
          lastDocRef.current = result.lastDoc;
        })
        .catch(() => {
          if (!cancelled) setError(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      setIsSearchMode(true);
      setError(false);
      searchReferencesInBrand(brandId, term, PAGE_SIZE)
        .then((results) => {
          if (cancelled) return;
          setReferences(results);
          setHasMore(false); // client-side search returns a single capped page
        })
        .catch(() => {
          if (!cancelled) setError(true);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchTerm, brandId, isSearchMode]);

  const loadMore = async () => {
    if (!hasMore || loadingMore || isSearchMode) return;
    setLoadingMore(true);
    try {
      const result = await getReferencesForBrandPaginated(
        brandId,
        PAGE_SIZE,
        lastDocRef.current,
        SORT_OPTIONS[sortKey].sort,
      );
      setReferences((prev) => [...prev, ...result.references]);
      setHasMore(result.hasMore);
      lastDocRef.current = result.lastDoc;
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  // Row tap → re-fetch the FULL reference doc (list rows may carry incomplete
  // nested data, and EditMyReferencePanel needs complete objects for its
  // whole-replace save semantics) then open the edit modal. On failure we toast
  // and leave the list untouched (read-only behaviour preserved).
  const handleSelectReference = async (reference: WatchReference) => {
    if (loadingFullRefId) return;
    setLoadingFullRefId(reference.id);
    try {
      const fullRef = await getWatchReference(brandId, reference.id);
      if (!fullRef) {
        toast.error("Couldn't open this reference. Please try again.");
        return;
      }
      setSelectedReference(fullRef);
    } catch (err) {
      console.error('Failed to load reference for editing', err);
      toast.error("Couldn't open this reference. Please try again.");
    } finally {
      setLoadingFullRefId(null);
    }
  };

  // Save → reflect the saved doc in the in-memory list immediately. Replace the
  // matching row when it already exists (edit, or a follow-up edit after the
  // in-modal create→edit transition); prepend it when it's brand new (create),
  // and flag that a create landed so the close handler refreshes from server.
  // EditMyReferencePanel toasts success itself, and the panel owns closing —
  // for create it stays open in edit mode — so we don't close here.
  const handleSaved = (reference: WatchReference) => {
    setReferences((prev) => {
      if (prev.some((r) => r.id === reference.id)) {
        return prev.map((r) => (r.id === reference.id ? reference : r));
      }
      createdInModalRef.current = true;
      return [reference, ...prev];
    });
  };

  // Create-modal close: if a create landed while it was open, re-query the
  // first page so server-computed fields (timestamps, normalized ref) replace
  // the optimistic row. Otherwise just close.
  const handleCloseCreate = () => {
    setIsCreating(false);
    if (createdInModalRef.current) {
      createdInModalRef.current = false;
      reloadFirstPage();
    }
  };

  // Duplicate-ref affordance from the create panel: close create, fetch the
  // conflicting doc, and open it in the edit modal.
  const handleEditExisting = async (referenceId: string) => {
    setIsCreating(false);
    createdInModalRef.current = false;
    try {
      const fullRef = await getWatchReference(brandId, referenceId);
      if (!fullRef) {
        toast.error("Couldn't open that reference. Please try again.");
        return;
      }
      setSelectedReference(fullRef);
    } catch (err) {
      console.error('Failed to load existing reference for editing', err);
      toast.error("Couldn't open that reference. Please try again.");
    }
  };

  // Changing the sort returns the user to the full browse list (clears search)
  // and re-queries from page 1. Clearing search state here keeps the browse
  // effect — keyed on [brandId, sortKey] — as the single owner of that fetch;
  // the empty-term branch of the search effect sees isSearchMode=false next
  // render and bails, so the two don't race.
  const handleSortChange = (nextKey: SortKey) => {
    if (nextKey === sortKey) return;
    setSearchTerm('');
    setIsSearchMode(false);
    setSortKey(nextKey);
  };

  const badgeCount = isSearchMode ? references.length : totalCount;

  // createdAt-based sorts (newest/oldest) can silently drop refs that lack a
  // creation date (Firestore excludes docs missing the orderBy field), so warn.
  const showCreatedAtWarning = SORT_OPTIONS[sortKey].sort.field === 'createdAt';

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
      <div className='px-6 py-4 border-b border-gray-200'>
        <div className='flex items-center justify-between gap-4'>
          <h2 className='text-base font-semibold text-gray-900'>
            Your References
            {badgeCount != null && (
              <span className='ml-2 text-sm font-normal text-gray-400'>
                · {badgeCount.toLocaleString('en-GB')}
              </span>
            )}
          </h2>
          <div className='flex items-center gap-2 flex-shrink-0'>
            <label htmlFor='ref-sort' className='sr-only'>
              Sort references
            </label>
            <select
              id='ref-sort'
              value={sortKey}
              onChange={(e) => handleSortChange(e.target.value as SortKey)}
              className='border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
              aria-label='Sort references'
            >
              {(Object.keys(SORT_OPTIONS) as SortKey[]).map((key) => (
                <option key={key} value={key}>
                  {SORT_OPTIONS[key].label}
                </option>
              ))}
            </select>
            <button
              type='button'
              onClick={() => {
                createdInModalRef.current = false;
                setIsCreating(true);
              }}
              className='px-3 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700'
            >
              + New reference
            </button>
          </div>
        </div>
        <input
          type='search'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search by reference, model, product or collection…'
          className='mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
        />
      </div>

      {showCreatedAtWarning && (
        <div className='mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900'>
          Some references may not appear in this order. Older or bulk-imported
          references that don't have a creation date are excluded. Switch to{' '}
          <strong>Reference number</strong> to see all.
        </div>
      )}

      {error ? (
        <div className='px-6 py-10 text-center text-sm text-red-600'>
          Couldn't load references. Please try again.
        </div>
      ) : loading || searching ? (
        <div className='px-6 py-10 text-center text-sm text-gray-400'>
          {searching ? 'Searching…' : 'Loading references…'}
        </div>
      ) : references.length === 0 ? (
        <div className='px-6 py-10 text-center text-sm text-gray-500'>
          {isSearchMode
            ? 'No references match your search.'
            : 'No references yet for this brand'}
        </div>
      ) : (
        <>
          <ul className='divide-y divide-gray-100'>
            {references.map((ref) => (
              <ReferenceRow
                key={ref.id}
                reference={ref}
                onSelect={handleSelectReference}
                loading={loadingFullRefId === ref.id}
              />
            ))}
          </ul>
          {!isSearchMode && hasMore && (
            <div className='px-6 py-4 border-t border-gray-100 text-center'>
              <button
                type='button'
                onClick={loadMore}
                disabled={loadingMore}
                className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50'
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {selectedReference && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedReference(null)}
          title='Edit Reference'
          size='lg'
        >
          <EditMyReferencePanel
            brandId={brandId}
            mode='edit'
            reference={selectedReference}
            onSaved={handleSaved}
            onClose={() => setSelectedReference(null)}
            onEditExisting={handleEditExisting}
          />
        </Modal>
      )}

      {isCreating && (
        <Modal
          isOpen={true}
          onClose={handleCloseCreate}
          title='New Reference'
          size='lg'
        >
          <EditMyReferencePanel
            brandId={brandId}
            mode='create'
            onSaved={handleSaved}
            onClose={handleCloseCreate}
            onEditExisting={handleEditExisting}
          />
        </Modal>
      )}
    </div>
  );
}
